/**
 * Reply to a vendor's answer, from the outreach row. A vendor who quoted
 * gets a thank-you; a vendor who declined gets the "why" questions (not
 * their alloy, not this size, not in stock, coming in two weeks?). The
 * proposal is shown ready to edit - adapted from the operator's earlier
 * edits for this vendor when there are any - and goes out from the
 * operator's own mailbox (default, team in copy) or from the team's
 * procurement address, inside the vendor's thread. What the operator
 * changes is kept and shapes the next proposal.
 */
import { useEffect, useState } from "react";
import {
  VendorReplyDraft,
  VendorReplySender,
  fetchVendorFollowUpDraft,
  fetchVendorReplyDraft,
  RfqVendorRequestRow,
  sendVendorFollowUp,
  sendVendorReply,
} from "./api";

const KIND_TITLE: { [kind: string]: string } = {
  "thank-you": "thank them for the quote",
  "ask-why": "ask why they declined",
  "follow-up": "follow up, they have not answered",
};

/** "reply": answer what the vendor wrote. "follow-up": chase a vendor who has not answered, now, with the scheduler's text. */
export type VendorPanelMode = "reply" | "follow-up";

/** The panel title: what this reply does, read from the proposal when it was written around the vendor's words. */
const titleFor = (draft: VendorReplyDraft | null, kind: string): string => {
  if (draft?.basis === "vendor:offer") return "say yes to what they offered";
  if (draft?.basis === "vendor:question") return "answer their question";
  if (draft?.basis === "vendor:no") return "thank them and ask what their no leaves open";
  if (draft?.basis === "vendor:quote") return "thank them for the quote";
  return KIND_TITLE[kind] || "reply";
};

const basisNote = (draft: VendorReplyDraft, vendor: string): string => {
  const learned = draft.examplesUsed > 0 ? ` Wording follows ${draft.examplesUsed} earlier repl${draft.examplesUsed === 1 ? "y" : "ies"} operators wrote by hand.` : "";
  if (draft.basis.startsWith("vendor:")) {
    const what = draft.gist ? `they ${draft.gist}` : "the proposal answers their email";
    const does =
      draft.basis === "vendor:offer"
        ? "The proposal says yes instead of asking why they declined."
        : draft.basis === "vendor:question"
          ? "The proposal answers it."
          : draft.basis === "vendor:no"
            ? "The proposal is short, like their answer."
            : "The proposal thanks them and asks only what needs clarifying.";
    return `Written from what ${vendor} wrote: ${what}. ${does}${learned}`;
  }
  if (draft.basis === "edits:vendor" || draft.basis === "edits:kind") {
    return `Proposal adapted from ${draft.examplesUsed} earlier repl${draft.examplesUsed === 1 ? "y" : "ies"} ${draft.basis === "edits:vendor" ? `you edited for ${vendor}` : "operators edited in similar cases"}.`;
  }
  return "Standard proposal (the vendor's words were not kept or could not be read; no earlier edits to learn from yet).";
};

export function VendorReplyPanel({
  rfqId,
  request,
  mode = "reply",
  onSent,
  onClose,
}: {
  rfqId: string;
  request: RfqVendorRequestRow;
  mode?: VendorPanelMode;
  onSent: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<VendorReplyDraft | null>(null);
  const [followUpNumber, setFollowUpNumber] = useState(0);
  const [proposed, setProposed] = useState<{ subject: string; body: string } | null>(null);
  const [sender, setSender] = useState<VendorReplySender>("operator");
  const [senders, setSenders] = useState<{ operator: string; team: string } | null>(null);
  const [from, setFrom] = useState("");
  const [cc, setCc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  /** Bumped by "Write another reply" after a send: a fresh proposal is fetched. */
  const [round, setRound] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    const fetching =
      mode === "follow-up"
        ? fetchVendorFollowUpDraft(rfqId, request.id, sender).then((r) => {
            setFollowUpNumber(r.draft.followUpNumber);
            return r;
          })
        : fetchVendorReplyDraft(rfqId, request.id, sender);
    fetching
      .then((r) => {
        if (!alive) return;
        // Keep the operator's edits when only the sender changed.
        setDraft((cur) => (cur ? { ...r.draft, subject: cur.subject, body: cur.body, to: cur.to } : r.draft));
        setProposed((cur) => cur || { subject: r.draft.subject, body: r.draft.body });
        setFrom(r.from);
        setCc(r.cc);
        setSenders(r.senders);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // The draft is fetched once per request (and again per round); a sender change only re-reads From/CC.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfqId, request.id, sender, mode, round]);

  const another = () => {
    setDone("");
    setDraft(null);
    setProposed(null);
    setRound((n) => n + 1);
  };

  const send = async () => {
    if (!draft) return;
    setSending(true);
    setError("");
    try {
      const r =
        mode === "follow-up"
          ? await sendVendorFollowUp(rfqId, request.id, draft, { sender })
          : await sendVendorReply(rfqId, request.id, draft, {
              sender,
              proposed: proposed || undefined,
            });
      setDone(
        r.sent
          ? `Sent from ${r.from} to ${r.to.join(", ")}${cc ? `, copy to ${cc}` : ""}.${
              r.learned ? " Your edits were kept for the next proposal." : ""
            }${mode === "follow-up" ? " The scheduler's next chase, if any, now counts from this email." : ""}`
          : "Recorded, but external email is off in this environment so nothing went out.",
      );
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  const kind = mode === "follow-up" ? "follow-up" : draft?.kind || (request.status === "responded" ? "thank-you" : "ask-why");
  const previous = mode === "follow-up" ? null : request.vendorReply || request.declineReply;
  const lastEmailAt = request.lastEmailAt || request.sentAt;

  return (
    <div
      className="mt-2 w-full rounded border border-blue-200 bg-blue-50/40 p-3 text-xs text-gray-800"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-gray-900">
          Reply to {request.supplierName}: {titleFor(draft, kind)}
        </span>
        <button type="button" className="text-gray-500 hover:text-gray-800" onClick={onClose}>
          Close
        </button>
      </div>
      {request.responseText && (
        <details className="mt-1">
          <summary className="cursor-pointer text-gray-600">
            What the vendor wrote
            {request.responseReceivedAt || request.respondedAt
              ? ` (received ${new Date(request.responseReceivedAt || request.respondedAt || "").toLocaleString()})`
              : ""}
          </summary>
          <div className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-white p-2 text-gray-800">
            {request.responseFrom && <div className="mb-1 text-gray-500">From {request.responseFrom}</div>}
            {request.responseText}
          </div>
        </details>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="text-gray-600">Send from</span>
        <label className="flex cursor-pointer items-center gap-1">
          <input
            type="radio"
            name={`sender-${request.id}`}
            checked={sender === "operator"}
            onChange={() => setSender("operator")}
          />
          <span>
            my mailbox{senders?.operator ? ` (${senders.operator})` : ""}
          </span>
        </label>
        <label className="flex cursor-pointer items-center gap-1">
          <input
            type="radio"
            name={`sender-${request.id}`}
            checked={sender === "team"}
            onChange={() => setSender("team")}
          />
          <span>{senders?.team || "procurement@kono.ai"}</span>
        </label>
      </div>
      <p className="mt-1 text-gray-600">
        Goes out from {from || "..."}
        {cc ? ` with ${cc} in copy` : ""}, in the vendor's thread. Edit anything before
        sending; your edits teach the next proposal.
      </p>
      {draft && mode === "reply" && <p className="mt-1 text-gray-500">{basisNote(draft, request.supplierName)}</p>}
      {mode === "follow-up" && (
        <p className="mt-1 text-gray-500">
          {lastEmailAt ? `Last email to them ${new Date(lastEmailAt).toLocaleString()}` : "No email on record yet"}
          {request.followUpsSent ? `, ${request.followUpsSent} follow-up${request.followUpsSent === 1 ? "" : "s"} so far` : ""}.
          {followUpNumber ? ` This is follow-up #${followUpNumber}, the text the scheduler would send.` : ""}
          {request.nextFollowUpAt ? ` The scheduler planned the next one for ${new Date(request.nextFollowUpAt).toLocaleString()}; sending now moves it out.` : ""}
        </p>
      )}
      {previous && (
        <p className="mt-1 text-amber-800">
          A reply was already sent {new Date(previous.at).toLocaleString()} by {previous.by}.
          Sending again adds a second message.
        </p>
      )}
      {loading && !draft && (
        <p className="mt-2 text-gray-500">
          Preparing the proposal: the text is written from what {request.supplierName} said and from the team's earlier replies, which takes a few seconds. The To, Subject and Message fields appear here when it is ready.
        </p>
      )}
      {error && <p className="mt-2 text-red-700">{error}</p>}
      {draft && !done && (
        <div className="mt-2 flex flex-col gap-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-gray-600">To</span>
            <input
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              value={draft.to.join(", ")}
              onChange={(e) =>
                setDraft({ ...draft, to: e.target.value.split(/[,;\s]+/).filter(Boolean) })
              }
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-gray-600">Subject</span>
            <input
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-gray-600">Message</span>
            <textarea
              className="h-56 rounded border border-gray-300 bg-white px-2 py-1 font-mono text-xs"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={sending || loading || !draft.to.length || !draft.body.trim()}
              onClick={send}
            >
              {sending ? "Sending..." : mode === "follow-up" ? "Send follow-up" : "Send reply"}
            </button>
            <button type="button" className="text-gray-600 hover:underline" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {done && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="text-green-800">{done}</p>
          <button type="button" className="rounded border border-blue-300 bg-white px-2 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-50" onClick={another}>
            Write another {mode === "follow-up" ? "follow-up" : "reply"}
          </button>
          <button type="button" className="text-gray-600 hover:underline" onClick={onClose}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}

/** @deprecated use VendorReplyPanel */
export const DeclineReplyPanel = VendorReplyPanel;
